/**
 * ─── Domain-Based Branding & Navigation ──────────────────────
 * Maps domain names to brand identity and sidebar focus.
 *
 * When a user visits:
 *   hris-a7n.pages.dev     → Shows "HRIS" branding, only HRIS sidebar items
 *   pawnshop.example.com   → Shows "Pawnshop" branding, only Pawnshop items
 *   arena.example.com      → Shows "Arena" branding, only Arena items
 *   (any other domain)     → Shows "CoopERP" branding, all modules
 */

export interface DomainBrand {
  /** Short code for the logo badge */
  shortName: string
  /** Full display name */
  fullName: string
  /** Logo background color (tailwind class) */
  color: string
  /** Focus group to filter sidebar (null = show all) */
  focusGroup: string | null
  /** Page title suffix */
  titleSuffix: string
}

const DOMAIN_BRAND_MAP: Record<string, DomainBrand> = {
  'hris': {
    shortName: 'HR',
    fullName: 'HRIS Payroll',
    color: 'bg-teal-600',
    focusGroup: 'HRIS',
    titleSuffix: 'HRIS',
  },
  'pawnshop': {
    shortName: 'PS',
    fullName: 'Pawnshop',
    color: 'bg-emerald-600',
    focusGroup: 'Pawnshop',
    titleSuffix: 'Pawnshop',
  },
  'arena': {
    shortName: 'AR',
    fullName: 'Fighting Arena',
    color: 'bg-red-600',
    focusGroup: 'Arena',
    titleSuffix: 'Arena',
  },
  'laundry': {
    shortName: 'LW',
    fullName: 'Laundry Shop',
    color: 'bg-indigo-600',
    focusGroup: 'Laundry',
    titleSuffix: 'Laundry',
  },
  'clinic': {
    shortName: 'CL',
    fullName: 'Clinic',
    color: 'bg-rose-600',
    focusGroup: 'Clinic',
    titleSuffix: 'Clinic',
  },
  'fastfood': {
    shortName: 'FF',
    fullName: 'Crispy King',
    color: 'bg-amber-600',
    focusGroup: 'Fast Food',
    titleSuffix: 'Fast Food',
  },
  'driving-school': {
    shortName: 'DS',
    fullName: 'Driving School',
    color: 'bg-red-600',
    focusGroup: 'Driving School',
    titleSuffix: 'Driving School',
  },
  'water-station': {
    shortName: 'WS',
    fullName: 'Water Station',
    color: 'bg-cyan-600',
    focusGroup: 'Water Station',
    titleSuffix: 'Water Station',
  },
  'jewelry': {
    shortName: 'JW',
    fullName: 'Jewelry Shop',
    color: 'bg-purple-600',
    focusGroup: 'Jewelry',
    titleSuffix: 'Jewelry',
  },
  'cooperative': {
    shortName: 'CE',
    fullName: 'CoopERP',
    color: 'bg-green-600',
    focusGroup: null,
    titleSuffix: 'Cooperative Management System',
  },
  'cooperp': {
    shortName: 'CE',
    fullName: 'CoopERP',
    color: 'bg-green-600',
    focusGroup: null,
    titleSuffix: 'Cooperative Management System',
  },
}

const DEFAULT_BRAND: DomainBrand = {
  shortName: 'CE',
  fullName: 'CoopERP',
  color: 'bg-green-600',
  focusGroup: null,
  titleSuffix: 'Cooperative Management System',
}

/**
 * Detect the brand from the current domain.
 * Returns the DomainBrand matching a keyword in the hostname,
 * or the default CoopERP brand.
 */
export function getDomainBrand(): DomainBrand {
  try {
    const hostname = window.location.hostname.toLowerCase()
    for (const [keyword, brand] of Object.entries(DOMAIN_BRAND_MAP)) {
      if (hostname.includes(keyword)) {
        return brand
      }
    }
  } catch { /* SSR guard */ }
  return DEFAULT_BRAND
}

/**
 * Get the focus group from the domain, if any.
 * Returns null if the domain maps to "show all".
 */
export function getDomainFocusGroup(): string | null {
  return getDomainBrand().focusGroup
}

/**
 * Get the page title based on the domain brand.
 */
export function getBrandedTitle(): string {
  const brand = getDomainBrand()
  return `CoopERP — ${brand.titleSuffix}`
}
